import { g, layers, Tool } from '@hcie/core';
import { getMaskBounds } from './selection/index';
import { EventBus } from '@hcie/shared';
import { setFloatingContent, SelectionTransformUI, moveSelectionContent } from './selection/transformer';

declare global {
  interface Window {
    getCanvasTransform?: () => any;
    updateToolState?: (state: string) => void;
    extractSelectionBorder?: (canvas: HTMLCanvasElement | OffscreenCanvas) => any;
    renderLayers?: (liveCanvas?: HTMLCanvasElement | OffscreenCanvas) => void;
    copySelection: typeof copySelection;
    cutSelection: typeof cutSelection;
    pasteSelection: typeof pasteSelection;
    duplicateSelection: typeof duplicateSelection;
  }
}

/**
 * Clipboard and Edit Operations for Selection
 * Handles Cut, Copy, Paste, Move Selection.
 */


    // Internal clipboard (in-memory)
    let internalClipboard: { width: number, height: number, data: ImageData } | null = null;

    /**
     * Copy current selection to clipboard.
     * If no selection, copy entire active layer? Krita copies selection.
     */
export function copySelection() {
        if (!g.isSelectionActive || !g.selectionCanvas) {
            console.log("No selection to copy");
            return;
        }

        const activeLayer = layers[g.activeLayerIndex];
        if (!activeLayer) return;

        // Get bounds of selection
        const bounds = getMaskBounds(g.selectionCanvas);
        if (!bounds) return;

        // Extract pixel data from active layer within bounds, masked by selection
        const ctx = activeLayer.ctx;
        const imgData = ctx.getImageData(bounds.x, bounds.y, bounds.w, bounds.h);

        // We need to apply the selection mask alpha to the copied data?
        // Yes, copied data should be transparent where not selected.
        const maskCtx = g.selectionCanvas.getContext("2d");
        if (!maskCtx) return;
        const maskData = maskCtx.getImageData(bounds.x, bounds.y, bounds.w, bounds.h);

        const finalData = new ImageData(bounds.w, bounds.h);

        for (let i = 0; i < imgData.data.length; i += 4) {
            const alpha = maskData.data[i + 3] / 255;
            finalData.data[i] = imgData.data[i];
            finalData.data[i + 1] = imgData.data[i + 1];
            finalData.data[i + 2] = imgData.data[i + 2];
            // Multiply layer alpha by mask alpha
            finalData.data[i + 3] = imgData.data[i + 3] * alpha;
        }

        internalClipboard = {
            width: bounds.w,
            height: bounds.h,
            data: finalData
        };
        console.log("Copied selection to clipboard", internalClipboard);
    }

    /**
     * Cut: Copy + Erase selection.
     */
export function cutSelection() {
        if (!g.isSelectionActive) return;
        copySelection();

        // Erase selected pixels
        // We can use the selection mask with 'destination-out' composite op?
        // Yes.
        const activeLayer = layers[g.activeLayerIndex];
        if (!activeLayer || activeLayer.locked) return;

        const ctx = activeLayer.ctx;
        ctx.save();
        if (!g.selectionCanvas) return;
        ctx.globalCompositeOperation = "destination-out";
        ctx.drawImage(g.selectionCanvas as CanvasImageSource, 0, 0);
        ctx.restore();

        // Capture history
        // Capture history
        // TODO: History push
        EventBus.emit('RENDER_LAYERS');
    }

    /**
     * Paste: Paste clipboard content.
     * Creates a new layer with pasted content.
     */
    /**
     * Paste: Paste clipboard content.
     */
export function pasteSelection() {
        if (!internalClipboard) {
            console.log("Clipboard empty");
            return;
        }

        // Paste as Floating Content (Move Content tool behavior)
        const fCanvas = g.createCanvas(internalClipboard.width, internalClipboard.height);
        const fctx = fCanvas.getContext("2d");
        if (!fctx) return;
        fctx.putImageData(internalClipboard.data, 0, 0);

        // Center on screen
        // Center in Viewport simplified:
        // Visible area center in canvas coords:
        const container = document.getElementById('canvasScrollArea');
        let cx = g.image_width / 2;
        let cy = g.image_height / 2;

        if (container) {
            // Center of view
            const viewCX = container.scrollLeft + container.clientWidth / 2;
            const viewCY = container.scrollTop + container.clientHeight / 2;
            cx = viewCX / g.zoom;
            cy = viewCY / g.zoom;
        }

        const x = Math.round(cx - internalClipboard.width / 2);
        const y = Math.round(cy - internalClipboard.height / 2);

        // initialize floating content
        g.floatingContent = {
            canvas: fCanvas,
            x: x,
            y: y,
            angle: 0,
            scaleX: 1,
            scaleY: 1
        };

        // Switch to Move Content tool automatically
        g.current_tool = Tool.MoveContent;
        // Notify UI tool change if possible
        if (window.updateToolState) window.updateToolState('move-content'); // Hypothethical

        // Update selection mask to match pasted content alpha (strict 0/1)
        // This is important so the user "holds" the pasted object
        const newMask = g.createCanvas(g.image_width, g.image_height);
        const mctx = newMask.getContext("2d");
        if (!mctx) return;
        mctx.drawImage(fCanvas as CanvasImageSource, x, y);
        // Threshold alpha for mask
        const idata = mctx.getImageData(0, 0, g.image_width, g.image_height);
        const d = idata.data;
        for (let i = 3; i < d.length; i += 4) {
            d[i] = (d[i] > 0) ? 255 : 0;
        }
        mctx.putImageData(idata, 0, 0);

        g.selectionCanvas = newMask;
        g.isSelectionActive = true;
        g.selectionCanvas = newMask;
        g.isSelectionActive = true;
        
        if (typeof (window as any).extractSelectionBorder === 'function') {
            g.selectionBorder = (window as any).extractSelectionBorder(g.selectionCanvas);
        }

        // --- FIX #1351: Show Transform UI after Paste ---
        // Ensures the user sees selection handles and can immediately move/scale.
        let transformer = (window as any).transformUI as SelectionTransformUI;
        if (!transformer) {
            transformer = new SelectionTransformUI();
            (window as any).transformUI = transformer;
        }
        
        // Synchronize state for the transformer
        const activeLayer = layers[g.activeLayerIndex];
        if (activeLayer) {
            transformer.originalImageData = (activeLayer.ctx as CanvasRenderingContext2D).getImageData(0, 0, activeLayer.canvas.width, activeLayer.canvas.height);
            transformer.originalLayerIndex = g.activeLayerIndex;
            transformer.originalShapes = activeLayer.shapes ? [...activeLayer.shapes] : [];
        }

        transformer.show({
            x: x,
            y: y,
            w: internalClipboard.width,
            h: internalClipboard.height
        });

        EventBus.emit('RENDER_LAYERS');
        console.log("Pasted selection to clipboard and initialized transform UI", internalClipboard);
    }

    /**
     * Duplicate: Copy then Paste immediately (floating).
     */
export function duplicateSelection() {
        if (!g.isSelectionActive) return;
        copySelection();
        // Don't erase original (Ctrl+J behavior usually duplicates)
        pasteSelection();
        console.log("Duplicated selection");
    }

    // Expose







// TEMPORARY WINDOW BINDINGS
if (typeof window !== 'undefined') {
  Object.assign(window, {
    copySelection, cutSelection, pasteSelection, duplicateSelection, copyAndPastePixels, copyAndPasteVector
  });
}

/**
 * Copy selected pixels and paste them as floating content with transform UI.
 * Does NOT erase the original pixels (true copy behavior).
 */
export function copyAndPastePixels() {
    if (!g.isSelectionActive || !g.selectionCanvas) {
        console.log('No selection active for copy-paste.');
        return;
    }

    // STEP 1: Using moveSelectionContent with skipErase=true to extract pixels 
    // without removing them from background layer.
    moveSelectionContent(0, 0, true);

    // STEP 2: Shift the newly created floating content by 20px so user sees it as a duplicate
    const fc = g.floatingContent;
    if (fc) {
        fc.x += 20;
        fc.y += 20;
        
        // Update UI handles position
        const transformer = (window as any).transformUI as SelectionTransformUI;
        if (transformer) transformer.updatePosition();
    }

    // STEP 3: Ensure we are in MoveContent tool using the formal coordinator
    if (typeof (window as any).selectTool === 'function') {
        (window as any).selectTool(Tool.MoveContent);
    } else {
        g.current_tool = Tool.MoveContent;
        EventBus.emit('TOOL_CHANGED', { toolId: Tool.MoveContent.id });
    }
    
    // Double sync to ensure renderer and transformer see the same state 
    // (helps if core singleton isn't shared across bundles)
    (window as any).floatingContent = fc;
    g.floatingContent = fc;

    EventBus.emit('RENDER_LAYERS');
    console.log('Copied and pasted selection via moveSelectionContent(skipErase:true)');
}

export function copyAndPasteVector() {
    const vtm = (window as any).vectorToolManager;
    if (!vtm || !vtm.currentShape || !vtm.isEditing || !vtm.editTarget) {
        console.log("No vector shape is currently active for copying.");
        return;
    }

    const { layerIndex, shapeIndex } = vtm.editTarget;
    const activeLayer = layers[layerIndex];
    if (!activeLayer || !activeLayer.shapes) return;

    // 1. Clone the current shape
    const clonedShape = JSON.parse(JSON.stringify(vtm.currentShape));
    
    // 2. Add an offset to make it visible
    const offset = 20;
    clonedShape.x1 += offset;
    clonedShape.y1 += offset;
    clonedShape.x2 += offset;
    clonedShape.y2 += offset;
    
    // 3. Commit the original edit so it's saved in layer.shapes
    vtm.commitEdit();
    
    // 4. Push the cloned shape
    activeLayer.shapes.push(clonedShape);
    
    // 5. Start edit mode on the newly pasted shape
    // AI_STABILIZE: Use a more robust selection chain
    requestAnimationFrame(() => {
        if (!vtm) return;
        vtm.trySelectShapeAt(
            (clonedShape.x1 + clonedShape.x2) / 2, 
            (clonedShape.y1 + clonedShape.y2) / 2, 
            layerIndex
        );
        
        // Final fallback: Explicitly start edit if positional selection failed
        if (!vtm.isEditing && activeLayer.shapes) {
            vtm.startEdit(clonedShape, { layerIndex, shapeIndex: activeLayer.shapes.length - 1 });
        }
        EventBus.emit('RENDER_LAYERS');
    });
}
